/**
 * Wording that follows the tenant's privacy law.
 *
 * Three modes, read from tenants.regulations by regulationTermsMode:
 *  - dpdpa: DPDPA alone, or nothing recorded. DPDPA has no "sensitive" tier and
 *    calls the rights request a DPAR, so the console says "Personal Data" /
 *    "Non-Personal Data" and "DPAR".
 *  - mixed: DPDPA plus a regime that does use PII/SPI/DSAR (GDPR, CCPA and the
 *    like). Both vocabularies show: "Personal Data / PII", "Personal Data /
 *    SPI", "DPAR / DSAR", "data principal / data subject". The other law keeps
 *    a sensitive tier, so sensitive styling stays.
 *  - legacy: another regime without DPDPA keeps the labels it always had.
 * This is display only: scanner classes, stored classification values, enums
 * and API field values are never rewritten, so existing records and
 * integrations stay valid.
 *
 * The regulations come from tenants.regulations, delivered on
 * GET /v1/entitlements/current as tenant.regulations. A tenant with none
 * recorded, or a client talking to an older API, reads as DPDPA, matching the
 * consent console.
 */
import {
  Children,
  Fragment,
  cloneElement,
  createElement,
  isValidElement,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';

import { useEntitlements } from './use-entitlements';

export const PERSONAL_DATA_LABEL = 'Personal Data';
export const NON_PERSONAL_DATA_LABEL = 'Non-Personal Data';

/**
 * Regulation ids whose own vocabulary is PII / SPI / PHI / DSAR. Matched by
 * prefix because tenants.regulations carries article-level ids such as
 * GDPR_Art8. Sector and security frameworks (ISO_27001, SEBI_CSCRF, UIDAI) do
 * not define a data-class vocabulary and so do not change the wording.
 */
const OTHER_LAW_PREFIXES = ['GDPR', 'UK_GDPR', 'CCPA', 'CPRA', 'HIPAA', 'GLBA'];

export type RegulationTermsMode = 'dpdpa' | 'mixed' | 'legacy';

/** Stored values that denote personal data of some kind. */
const PERSONAL_VALUES = new Set(['PII', 'SPI', 'SPDI', 'PI', 'FI', 'PHI', 'PERSONAL']);

function hasPrefix(regulations: readonly string[], prefixes: readonly string[]): boolean {
  return regulations.some((r) => {
    const id = r.trim().toUpperCase();
    return prefixes.some((p) => id === p || id.startsWith(`${p}_`));
  });
}

/**
 * Which vocabulary a tenant reads. Another law without DPDPA is legacy, as
 * before; nothing recorded is DPDPA, as in the consent console.
 *
 * The evidence packs do not follow this mode (the API decides per pack): the
 * DPDPA pack is a statutory artefact and stays pure DPDPA even for a mixed
 * tenant, the GDPR and CCPA packs keep their own terms.
 */
export function regulationTermsMode(regulations: readonly string[] | null | undefined): RegulationTermsMode {
  if (!regulations || regulations.length === 0) return 'dpdpa';
  const otherLaw = hasPrefix(regulations, OTHER_LAW_PREFIXES);
  if (!hasPrefix(regulations, ['DPDPA'])) return otherLaw ? 'legacy' : 'dpdpa';
  return otherLaw ? 'mixed' : 'dpdpa';
}

export function useRegulationTermsMode(): RegulationTermsMode {
  const { entitlements } = useEntitlements();
  return regulationTermsMode(entitlements?.tenant?.regulations);
}

export function isPersonalClassification(value: string | null | undefined): boolean {
  if (!value) return false;
  return PERSONAL_VALUES.has(value.trim().toUpperCase());
}

const NON_PERSONAL_PATTERN = /^(non[-_ ]?personal|non[-_ ]?pii|public|none)$/i;
const SENSITIVE_PATTERN = /^(spi|spdi|phi|sensitive.*)$/i;

export const PERSONAL_DATA_PII_LABEL = `${PERSONAL_DATA_LABEL} / PII`;
export const PERSONAL_DATA_SPI_LABEL = `${PERSONAL_DATA_LABEL} / SPI`;

/**
 * Display label for a stored classification. In legacy mode the stored value
 * is returned untouched. In dpdpa mode every classification except an explicit
 * non-personal one is a personal category, and showing nothing would hide
 * data. In mixed mode the sensitive tier survives because the other law has
 * one, so the sensitive and plain personal classes read differently.
 */
export function classificationLabel(value: string | null | undefined, mode: RegulationTermsMode): string {
  const raw = value ?? '';
  if (mode === 'legacy') return raw;
  // Absent is not a classification: show nothing rather than assert
  // Non-Personal Data for a value nobody chose.
  if (!raw.trim()) return '';
  if (NON_PERSONAL_PATTERN.test(raw.trim())) return NON_PERSONAL_DATA_LABEL;
  if (mode === 'mixed') return SENSITIVE_PATTERN.test(raw.trim()) ? PERSONAL_DATA_SPI_LABEL : PERSONAL_DATA_PII_LABEL;
  return PERSONAL_DATA_LABEL;
}

/**
 * The governance category a finding carries (personal, personal_identifier,
 * sensitive_financial, sensitive_health, children, credential, device). The
 * personal and sensitive tiers collapse to Personal Data under DPDPA, and
 * read "Personal Data / PII" and "Personal Data / SPI" in mixed mode; children,
 * credential and device are not data classes of that kind and keep their name.
 */
export function governanceCategoryLabel(category: string, mode: RegulationTermsMode): string {
  if (mode === 'dpdpa' && /^(personal|sensitive)/i.test(category)) return PERSONAL_DATA_LABEL;
  if (mode === 'mixed') {
    if (/^personal/i.test(category)) return PERSONAL_DATA_PII_LABEL;
    if (/^sensitive/i.test(category)) return PERSONAL_DATA_SPI_LABEL;
  }
  return category.replace(/_/g, ' ');
}

/** "DPAR" under DPDPA, "DPAR / DSAR" when both laws apply, "DSAR" otherwise. */
export function rightsRequestTerm(mode: RegulationTermsMode): string {
  if (mode === 'dpdpa') return 'DPAR';
  return mode === 'mixed' ? 'DPAR / DSAR' : 'DSAR';
}

type ProseRule = readonly [RegExp, string | ((...groups: string[]) => string)];

/**
 * Rewrites prose that was authored with PII / DSAR wording. One ordered list
 * of rules instead of string edits scattered across screens; the first match
 * wins for overlapping phrases ("Sensitive PII" before "PII").
 */
const DPDPA_PROSE_RULES: ReadonlyArray<ProseRule> = [
  [/\b([Dd]ata) ([Ss])ubject/g, (_m, d: string, s: string) => `${d} ${s === 'S' ? 'Principal' : 'principal'}`],
  [/\bDSARs\b/g, 'DPARs'],
  [/\bDSAR\b/g, 'DPAR'],
  [/\b[Nn]on-PII\b/g, NON_PERSONAL_DATA_LABEL],
  // Counts of critical/high-sensitivity assets keep their meaning: DPDPA has
  // no sensitive tier, but the risk ranking the console computes still exists.
  [/\bSensitive (?:PII|SPI|SPDI) assets\b/g, `High-risk ${PERSONAL_DATA_LABEL} assets`],
  [/\bSensitive (?:PII|SPI|SPDI)\b/g, PERSONAL_DATA_LABEL],
  [/\bSensitive Personal (?:Data|Information)\b/gi, PERSONAL_DATA_LABEL],
  [/\bPersonally Identifiable Information\b/gi, PERSONAL_DATA_LABEL],
  [/\b(?:PII|SPDI|SPI|PHI)\b/g, PERSONAL_DATA_LABEL],
];

/**
 * Mixed mode keeps both vocabularies. Every rule that prefixes a term carries
 * a lookbehind for its own output, so text that was already rewritten (or was
 * authored with the combined wording) is not rewritten twice.
 */
const MIXED_PROSE_RULES: ReadonlyArray<ProseRule> = [
  [
    /(?<!rincipal \/ )(?<!rincipals \/ )\b([Dd]ata) ([Ss])ubject(s?)\b/g,
    (_m, d: string, s: string, plural: string) => {
      const [principal, subject] = s === 'S' ? ['Principal', 'Subject'] : ['principal', 'subject'];
      return `${d} ${principal}${plural} / ${d} ${subject}${plural}`;
    },
  ],
  [/(?<!DPARs \/ )\bDSARs\b/g, 'DPARs / DSARs'],
  [/(?<!DPAR \/ )\bDSAR\b/g, 'DPAR / DSAR'],
  [/\b[Nn]on-PII\b/g, NON_PERSONAL_DATA_LABEL],
  [/\bSensitive (?:PII|SPI|SPDI) assets\b/g, `High-risk ${PERSONAL_DATA_SPI_LABEL} assets`],
  [/\bSensitive (?:PII|SPI|SPDI)\b/g, PERSONAL_DATA_SPI_LABEL],
  [/\bSensitive Personal (?:Data|Information)\b/gi, PERSONAL_DATA_SPI_LABEL],
  [/\bPersonally Identifiable Information\b/gi, PERSONAL_DATA_PII_LABEL],
  [/(?<!Personal Data \/ )\b(?:SPDI|SPI|PHI)\b/g, PERSONAL_DATA_SPI_LABEL],
  [/(?<!Personal Data \/ )\bPII\b/g, PERSONAL_DATA_PII_LABEL],
];

export function dpdpaText(text: string, mode: RegulationTermsMode): string {
  if (mode === 'legacy') return text;
  const rules = mode === 'mixed' ? MIXED_PROSE_RULES : DPDPA_PROSE_RULES;
  let out = text;
  for (const [pattern, replacement] of rules) {
    out = out.replace(pattern, replacement as string);
  }
  return out;
}

/**
 * Hook form for components: `const tx = useRegulationText();` then `tx('...PII...')`.
 * The returned function is stable until the tenant's regulations change.
 */
export function useRegulationText(): (text: string) => string {
  const mode = useRegulationTermsMode();
  return useCallback((text: string) => dpdpaText(text, mode), [mode]);
}

export function useClassificationLabel(): (value: string | null | undefined) => string {
  const mode = useRegulationTermsMode();
  return useMemo(() => (value) => classificationLabel(value, mode), [mode]);
}

/**
 * JSX form for text authored inline: `<Rx>Every PII column ...</Rx>`. Strings
 * anywhere in the subtree are rewritten, elements and expressions pass through
 * with their own string children rewritten, so it can wrap a whole paragraph.
 */
export function Rx({ children }: { children?: ReactNode }): ReactNode {
  const mode = useRegulationTermsMode();
  if (mode === 'legacy') return children;
  return createElement(Fragment, null, rewriteNode(children, mode));
}

function rewriteNode(node: ReactNode, mode: RegulationTermsMode): ReactNode {
  if (typeof node === 'string') return dpdpaText(node, mode);
  if (Array.isArray(node)) return Children.map(node, (child) => rewriteNode(child, mode));
  if (isValidElement<{ children?: ReactNode }>(node) && node.props.children !== undefined) {
    return cloneElement(node, undefined, rewriteNode(node.props.children, mode));
  }
  return node;
}
